import { createTheme, lighten } from '@mui/material';

const baseShadows = {
  z1: '0px 1px 2px ',
  z8: '0px 8px 16px ',
  z12: '0px 12px 24px -4px ',
  z16: '0px 16px 32px -4px ',
  z20: '0px 20px 40px -4px ',
  z24: '0px 24px 48px ',
  color: 'rgba(145, 158, 171, 0.16)',
};

export const customTheme = createTheme({
  typography: {
    h1: {
      fontFamily: 'Source Sans Pro',
    },
    h2: {
      fontFamily: 'Source Sans Pro',
    },
    h3: {
      fontFamily: 'Source Sans Pro',
      fontWeight: 'bold',
      opacity: '0.8',
    },
    h4: {
      fontFamily: 'Source Sans Pro',
      opacity: '0.8',
    },
    h5: {
      fontFamily: 'Source Sans Pro',
      opacity: '0.8',
    },
    h6: {
      fontFamily: 'Source Sans Pro',
      opacity: '0.8',
    },
    subtitle1: {
      fontFamily: 'Source Sans Pro',
      opacity: '0.8',
    },
    subtitle2: {
      fontFamily: 'Source Sans Pro',
      opacity: '0.8',
    },
    body1: {
      fontFamily: 'Karla',
    },
    body2: {
      fontFamily: 'Karla',
    },
    button: {
      fontFamily: 'Karla',
      textTransform: 'none',
    },
    caption: {
      fontFamily: 'Karla',
    },
    overline: {
      fontFamily: 'Karla',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: '#2648E8',
      hover: '#0e8f76',
      dark: '#1327A7',
      selected: '#F4F6F8',
      contrastText: '#ffffff',
      gradient: 'linear-gradient(135deg, #5A46FB 0%, #1E95FF 100%)',
      transparent16: 'rgba(38, 72, 232, 0.16)',
    },
    secondary: {
      main: '#1E95FF',
      hover: '#1e5fff',
      contrastText: '#ffffff',
      gradient:
        'linear-gradient(135deg, #4FD5D9 28.13%, #3A2BE3 100%, #3A2BE3 100%)',
    },
    info: {
      light: '#9E8FFD',
      main: '#5A46FB',
      dark: '#2F23B4',
      contrastText: 'rgba(0, 0, 0, 0.87)',
      gradient: 'linear-gradient(135deg, #15F06E 0%, #1E95FF 100%)',
    },
    success: {
      light: '#71FAB7',
      main: '#0FCEA9',
      dark: '#068B8B',
      contrastText: 'rgba(0, 0, 0, 0.87)',
      gradient: 'linear-gradient(135deg, #15F06E 0%, #1E95FF 100%)',
    },
    warning: {
      light: '#FFDB83',
      main: '#FFAB00',
      dark: '#B77119',
      contrastText: 'rgba(0, 0, 0, 0.87)',
      gradient: 'linear-gradient(135deg, #FFAB00 0%, #FFF6D6 100%)',
    },
    tertiary: {
      main: '#1E95FF',
      hover: '#1e5fff',
      contrastText: '#ffffff',
    },
    error: {
      light: '#FDAC9D',
      main: '#F95C5E',
      dark: '#B32E47',
      contrastText: '#fff',
      gradient: 'linear-gradient(135deg, #F95C5E 0%, #FFAB00 100%)',
    },
    disabled: {
      main: '#bfc0d1',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#212B36',
      secondary: '#637381',
      disabled: '#919EAB',
      hint: 'rgba(0, 0, 0, 0.38)',
    },
    actionStates: {
      active: '#637381',
      hover: 'rgba(145, 158, 171, 0.08)',
      selected: 'rgba(145, 158, 171, 0.16)',
      disabled: 'rgba(145, 158, 171, 0.8)',
      disabledBackground: 'rgba(145, 158, 171, 0.24)',
      focus: 'rgba(145, 158, 171, 0.24)',
    },
    shadow: {
      z1: `${baseShadows.z1} ${baseShadows.color}`,
      z8: `${baseShadows.z8} ${baseShadows.color}`,
      z12: `${baseShadows.z12} ${baseShadows.color}`,
      z16: `${baseShadows.z16} ${baseShadows.color}`,
      z20: `${baseShadows.z20} ${baseShadows.color}`,
      z24: `${baseShadows.z24} ${baseShadows.color}`,
      card: '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px rgba(145, 158, 171, 0.2)',
      button: '0px 8px 16px rgba(38, 72, 232, 0.24)',
      dialog: '-40px 40px 80px -8px rgba(145, 158, 171, 0.24)',
      dropdown:
        '-20px 20px 40px -4px rgba(145, 158, 171, 0.24), 0px 0px 2px rgba(145, 158, 171, 0.24)',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
      variants: [
        {
          props: { color: 'warning' },
          style: ({ theme }) => ({
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.warning.light,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.warning.dark,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.warning.main,
              borderWidth: '2px',
            },
          }),
        },
      ],
    },
    MuiInputLabel: {
      variants: [
        {
          props: { color: 'warning' },
          style: ({ theme }) => ({
            color: theme.palette.warning.main,
            '&.Mui-focused': {
              color: theme.palette.warning.main,
            },
          }),
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          '&.MuiButton-contained': {
            '&.MuiButton-containedPrimary': {
              background: theme.palette.primary.gradient,
            },
            '&.MuiButton-containedSecondary': {
              background: theme.palette.secondary.main,
            },
            '&.MuiButton-containedSuccess': {
              background: theme.palette.success.main,
            },
            '&.MuiButton-containedWarning': {
              background: theme.palette.warning.main,
            },
            '&.MuiButton-containedError': {
              background: theme.palette.error.main,
            },
            '&.Mui-disabled': {
              background: theme.palette.actionStates.disabledBackground,
              color: theme.palette.actionStates.disabled,
            },
          },
        }),
        colorInherit: {
          borderColor: 'rgba(0, 0, 0, 0.23)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '40px',
          backgroundColor: lighten(theme.palette.primary.main, 0.95),
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 30,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
        sizeSmall: {
          padding: '0px 1rem',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          fontWeight: '600',
          textTransform: 'uppercase',
          '&:hover': {
            color: theme.palette.primary.main,
          },
          '&.Mui-active': {
            color: theme.palette.primary.main,
          },
        }),
        icon: { color: 'inherit !important' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
        },
      },
      variants: [
        {
          props: { fullScreen: true },
          style: ({ theme }) => ({
            [theme.breakpoints.down('sm')]: {
              '& .MuiDialog-paper': { borderRadius: 0 },
            },
          }),
        },
      ],
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          opacity: 1,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(0.5, 1),
          borderRadius: '8px',
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
          '&.Mui-selected .MuiListItemIcon-root': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: () => ({
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(3px)',
          '&.MuiModal-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'none',
          },
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
          },
        }),
      },
    },

    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiAlert-root': {
            minWidth: '300px',
            padding: '12px',
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '22px',
            borderRadius: '8px',
            '.MuiSnackbarContent-action': {
              padding: '0 0 0 12px',
            },
            '&.MuiAlert-filled': {
              boxShadow: theme.palette.shadow.z8,
              backgroundColor: theme.palette.background.paper,
              '&.MuiAlert-filledInfo': {
                color: theme.palette.info.dark,
                '.MuiAlert-icon': {
                  color: theme.palette.info.main,
                  backgroundColor: theme.palette.info.light,
                  padding: '8px',
                  borderRadius: '8px',
                },
              },
              '&.MuiAlert-filledSuccess': {
                color: theme.palette.success.dark,
                '.MuiAlert-icon': {
                  color: theme.palette.success.main,
                  backgroundColor: theme.palette.success.light,
                  padding: '8px',
                  borderRadius: '8px',
                },
              },
              '&.MuiAlert-filledWarning': {
                color: theme.palette.warning.dark,
                '.MuiAlert-icon': {
                  color: theme.palette.warning.main,
                  backgroundColor: theme.palette.warning.light,
                  padding: '8px',
                  borderRadius: '8px',
                },
              },
              '&.MuiAlert-filledError': {
                color: theme.palette.error.dark,
                '.MuiAlert-icon': {
                  color: theme.palette.error.main,
                  backgroundColor: theme.palette.error.light,
                  padding: '8px',
                  borderRadius: '8px',
                },
              },
            },
          },
        }),
      },
    },
  },
});
